import { type FormEvent, useState } from "react";
import {
	Alert,
	AlertDescription,
	AlertTitle,
	Button,
	Checkbox,
	CheckboxField,
	CheckboxFieldContent,
	CheckboxFieldLabel,
	FormField,
	Input,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Spinner,
} from "@cognigy-internal/ui-component-library";
import { Eye, EyeOff } from "lucide-react";
import type { PublicConfig, SignupApi, SignupForm as SignupFormValues } from "lib/api";
import { listCountries } from "lib/countries";
import { EMAIL_PATTERN, passwordValid } from "lib/validation";
import { PasswordChecklist } from "./PasswordChecklist";
import { Recaptcha, type RecaptchaHandle } from "./Recaptcha";

type FieldErrors = Partial<Record<keyof SignupFormValues, string>>;

const EMPTY: SignupFormValues = {
	firstname: "",
	lastname: "",
	company: "",
	country: "",
	email: "",
	password: "",
	acceptedTerms: false,
};

// Built once so keystrokes elsewhere in the form do not re-render ~250 options.
const COUNTRY_ITEMS = listCountries().map(({ code, name }) => (
	<SelectItem
		key={code}
		value={name}
	>
		{name}
	</SelectItem>
));

function clientErrors(values: SignupFormValues): FieldErrors {
	const errors: FieldErrors = {};
	if (!values.firstname.trim()) errors.firstname = "Please enter your first name";
	if (!values.lastname.trim()) errors.lastname = "Please enter your last name";
	if (!values.company.trim()) errors.company = "Please enter your company name";
	if (!values.country) errors.country = "Please select your country";
	if (!EMAIL_PATTERN.test(values.email.trim()))
		errors.email = "Please enter a valid business email address";
	if (!passwordValid(values.password))
		errors.password = "Your password does not meet all requirements yet";
	if (!values.acceptedTerms) errors.acceptedTerms = "Please accept the trial terms to continue";
	return errors;
}

export interface SignupFormProps {
	api: SignupApi;
	config: PublicConfig;
	onCreated: (redirectUrl: string) => void;
	onFailed: (message: string) => void;
	loadCaptcha?: Parameters<typeof Recaptcha>[0]["load"];
}

export function SignupForm({ api, config, onCreated, onFailed, loadCaptcha }: SignupFormProps) {
	const [values, setValues] = useState(EMPTY);
	const [errors, setErrors] = useState<FieldErrors>({});
	const [submitted, setSubmitted] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [token, setToken] = useState("");
	const [captcha, setCaptcha] = useState<RecaptchaHandle>();
	const [captchaError, setCaptchaError] = useState("");
	const [busy, setBusy] = useState(false);

	const captchaRequired = config.recaptchaEnabled;
	const visibleErrors = submitted ? { ...clientErrors(values), ...errors } : errors;

	const update = <K extends keyof SignupFormValues>(key: K, value: SignupFormValues[K]) => {
		setValues(v => ({ ...v, [key]: value }));
		setErrors(current => {
			const next = { ...current };
			delete next[key];
			return next;
		});
	};

	const onSubmit = async (event: FormEvent) => {
		event.preventDefault();
		setSubmitted(true);
		setCaptchaError("");
		if (Object.keys(clientErrors(values)).length > 0) return;
		if (captchaRequired && !token) {
			setCaptchaError("Please confirm that you are not a robot");
			return;
		}
		setBusy(true);
		const result = await api.signup(
			{
				...values,
				firstname: values.firstname.trim(),
				lastname: values.lastname.trim(),
				company: values.company.trim(),
				email: values.email.trim(),
			},
			token,
		);
		switch (result.kind) {
			case "created":
				onCreated(result.redirectUrl);
				return;
			case "invalid":
				setBusy(false);
				captcha?.reset();
				if (result.field === "recaptchaToken")
					setCaptchaError("The captcha expired, please confirm it again");
				else if (result.field)
					setErrors(e => ({ ...e, [result.field as keyof SignupFormValues]: result.message }));
				else setCaptchaError(result.message);
				return;
			case "failed":
				onFailed(result.message);
		}
	};

	const field = (
		name: "firstname" | "lastname" | "company" | "email",
		label: string,
		props: Partial<Parameters<typeof Input>[0]>,
	) => (
		<FormField
			id={name}
			label={label}
			required
			errorText={visibleErrors[name]}
		>
			<Input
				id={name}
				name={name}
				value={values[name]}
				onChange={e => update(name, e.target.value)}
				aria-invalid={Boolean(visibleErrors[name])}
				aria-describedby={visibleErrors[name] ? `${name}-helper-text` : undefined}
				fieldSize="md"
				{...props}
			/>
		</FormField>
	);

	return (
		<form
			noValidate
			onSubmit={event => void onSubmit(event)}
			className="flex flex-col gap-4"
			aria-label="Create your free trial account"
		>
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
				{field("firstname", "First name", { autoComplete: "given-name", placeholder: "Jane" })}
				{field("lastname", "Last name", { autoComplete: "family-name", placeholder: "Doe" })}
			</div>
			{field("company", "Company name", {
				autoComplete: "organization",
				placeholder: "Your company",
			})}

			<FormField
				id="country"
				label="Country"
				required
				errorText={visibleErrors.country}
			>
				<Select
					value={values.country}
					onValueChange={value => update("country", value)}
					name="country"
					autoComplete="country-name"
				>
					<SelectTrigger
						id="country"
						className="w-full"
						aria-invalid={Boolean(visibleErrors.country)}
						aria-describedby={visibleErrors.country ? "country-helper-text" : undefined}
					>
						<SelectValue placeholder="Select country" />
					</SelectTrigger>
					<SelectContent>{COUNTRY_ITEMS}</SelectContent>
				</Select>
			</FormField>

			{field("email", "Business email", {
				type: "email",
				autoComplete: "email",
				inputMode: "email",
				placeholder: "name@company.com",
			})}

			<FormField
				id="password"
				label="Password"
				required
				errorText={visibleErrors.password}
			>
				<div className="relative">
					<Input
						id="password"
						name="password"
						type={showPassword ? "text" : "password"}
						autoComplete="new-password"
						value={values.password}
						onChange={e => update("password", e.target.value)}
						aria-invalid={Boolean(visibleErrors.password)}
						aria-describedby={
							visibleErrors.password ? "password-helper-text password-rules" : "password-rules"
						}
						placeholder="Create a password"
						fieldSize="md"
						className="pr-11"
					/>
					<Button
						type="button"
						variant="ghost"
						size="icon-sm"
						className="absolute top-1/2 right-1.5 -translate-y-1/2"
						aria-label={showPassword ? "Hide password" : "Show password"}
						aria-pressed={showPassword}
						onClick={() => setShowPassword(s => !s)}
					>
						{showPassword ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
					</Button>
				</div>
				<PasswordChecklist
					id="password-rules"
					password={values.password}
				/>
			</FormField>

			<div>
				<CheckboxField>
					<Checkbox
						id="acceptedTerms"
						checked={values.acceptedTerms}
						onCheckedChange={checked => update("acceptedTerms", checked === true)}
						aria-invalid={Boolean(visibleErrors.acceptedTerms)}
						aria-describedby="terms-description"
					/>
					<CheckboxFieldContent>
						<CheckboxFieldLabel htmlFor="acceptedTerms">
							I accept the{" "}
							<a
								href="https://www.cognigy.com/free-trial-terms-and-conditions"
								target="_blank"
								rel="noopener noreferrer"
								className="text-primary underline underline-offset-2"
							>
								Free Trial Terms and Conditions
							</a>{" "}
							to use NiCE Cognigy for evaluation purposes only. By submitting this form, I consent
							to receive information about NiCE Cognigy at the email address provided
						</CheckboxFieldLabel>
					</CheckboxFieldContent>
				</CheckboxField>
				{visibleErrors.acceptedTerms && (
					<p
						role="alert"
						className="text-error mt-1.5 text-sm"
					>
						{visibleErrors.acceptedTerms}
					</p>
				)}
			</div>

			<p
				id="terms-description"
				className="text-muted-foreground text-xs leading-relaxed"
			>
				You can opt out at any time using the unsubscribe link at the bottom of our email. We
				process your personal data in accordance with our{" "}
				<a
					href="https://www.cognigy.com/privacy-policy"
					target="_blank"
					rel="noopener noreferrer"
					className="text-primary underline underline-offset-2"
				>
					Privacy Policy
				</a>
				.
			</p>

			{captchaRequired && (
				<div>
					<Recaptcha
						siteKey={config.recaptchaSiteKey}
						onToken={t => {
							setToken(t);
							if (t) setCaptchaError("");
						}}
						onReady={setCaptcha}
						{...(loadCaptcha && { load: loadCaptcha })}
					/>
				</div>
			)}

			{captchaError && (
				<Alert
					variant="destructive"
					announce="assertive"
				>
					<AlertTitle>Almost there</AlertTitle>
					<AlertDescription>{captchaError}</AlertDescription>
				</Alert>
			)}

			<Button
				type="submit"
				size="lg"
				disabled={busy}
				className="w-full"
			>
				{busy ? (
					<>
						<Spinner
							size="sm"
							variant="white"
							aria-hidden
						/>
						Creating your trial…
					</>
				) : (
					"Get started"
				)}
			</Button>
		</form>
	);
}
